import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight, ChevronDown, Loader2, SlidersHorizontal, X,
  Landmark, Sword, Scroll,
} from "lucide-react";
import gkApi from "../api/gk";
import studyApi from "../api/studyProgress";
import SEOHead from "../components/SEOHead";
import { useAuth } from "../hooks/useAuth";
import { GkHeader, TopicCard } from "./GkLibrary";

const DIFFICULTIES = ["easy", "medium", "hard"];
const EXAMS = ["NDA", "CDS", "AFCAT", "CAPF", "TA", "SSB"];

/* ── History sections config ─────────────────────────────────────────────── */
const HISTORY_SECTIONS = [
  {
    key:      "ancient-india",
    label:    "Ancient India",
    period:   "Prehistoric – 647 AD",
    chapter:  "Chapter I",
    Icon:     Landmark,
    bg:       "#1C0A00",
    grad:     "linear-gradient(120deg, #1C0A00 0%, #7C2D12 45%, #C2410C 100%)",
    orb:      "#F97316",
    accent:   "#FED7AA",
    shadow:   "0 8px 32px -4px rgba(194,65,12,0.45), 0 2px 8px rgba(0,0,0,0.25)",
  },
  {
    key:      "medieval-india",
    label:    "Medieval India",
    period:   "650 AD – 1526 AD",
    chapter:  "Chapter II",
    Icon:     Sword,
    bg:       "#05082A",
    grad:     "linear-gradient(120deg, #05082A 0%, #1E3A8A 45%, #2563EB 100%)",
    orb:      "#60A5FA",
    accent:   "#BFDBFE",
    shadow:   "0 8px 32px -4px rgba(37,99,235,0.45), 0 2px 8px rgba(0,0,0,0.25)",
  },
  {
    key:      "modern-india",
    label:    "Modern India",
    period:   "1526 AD – Present",
    chapter:  "Chapter III",
    Icon:     Scroll,
    bg:       "#011510",
    grad:     "linear-gradient(120deg, #011510 0%, #065F46 45%, #059669 100%)",
    orb:      "#34D399",
    accent:   "#A7F3D0",
    shadow:   "0 8px 32px -4px rgba(5,150,105,0.45), 0 2px 8px rgba(0,0,0,0.25)",
  },
];

/* ── Section block for history ───────────────────────────────────────────── */
function HistorySection({ section, examTag, difficulty, navigate, category, progressMap }) {
  const [topics, setTopics]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen]       = useState(true);

  useEffect(() => {
    setLoading(true);
    gkApi
      .getByCategory(category, {
        subcategory: section.key,
        examTag: examTag || undefined,
        difficulty: difficulty || undefined,
        limit: 50,
      })
      .then(d => setTopics(d.topics || []))
      .catch(() => setTopics([]))
      .finally(() => setLoading(false));
  }, [section.key, examTag, difficulty, category]);

  const { Icon, label, period, chapter, grad, orb, accent, shadow } = section;

  return (
    <div className="mb-5 rounded-2xl overflow-hidden" style={{ boxShadow: shadow }}>
      {/* Section header */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full relative overflow-hidden"
        style={{ background: grad }}
      >
        {/* Atmospheric orb — top right */}
        <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full pointer-events-none"
          style={{ background: orb, opacity: 0.18, filter: "blur(60px)" }} />
        {/* Subtle orb — bottom left */}
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full pointer-events-none"
          style={{ background: orb, opacity: 0.12, filter: "blur(40px)" }} />

        {/* Content row */}
        <div className="relative flex items-center gap-5 px-6 py-6">

          {/* Icon box — glassmorphic */}
          <div className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{
              background: "rgba(255,255,255,0.12)",
              border: "1.5px solid rgba(255,255,255,0.22)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 4px 16px rgba(0,0,0,0.2)",
            }}
          >
            <Icon size={26} className="text-white" />
          </div>

          {/* Text */}
          <div className="flex-1 text-left min-w-0">
            <p className="text-[9px] font-extrabold tracking-[0.22em] uppercase mb-1"
              style={{ color: accent }}>
              {chapter}
            </p>
            <h3 className="font-sora font-black text-[1.25rem] leading-tight text-white">
              {label}
            </h3>
            <span className="inline-block mt-2 text-[11px] font-semibold px-2.5 py-0.5 rounded-full"
              style={{
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.2)",
                color: "rgba(255,255,255,0.78)",
              }}>
              {period}
            </span>
          </div>

          {/* Right — count + divider + chevron */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {!loading && (
              <div className="text-right">
                <p className="font-sora font-black text-3xl leading-none text-white">
                  {topics.length}
                </p>
                <p className="text-[9px] font-bold tracking-[0.18em] uppercase mt-0.5"
                  style={{ color: accent }}>
                  Topics
                </p>
              </div>
            )}
            <div className="w-px h-10 rounded-full" style={{ background: "rgba(255,255,255,0.18)" }} />
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.2)",
              }}>
              <ChevronDown size={15} className="text-white transition-transform duration-300"
                style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
            </div>
          </div>
        </div>
      </button>

      {/* Topics grid */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden bg-white"
          >
            <div className="p-4">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 size={22} className="animate-spin text-gray-300" />
                </div>
              ) : topics.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-6">No topics found.</p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {topics.map((topic, i) => (
                    <motion.div
                      key={topic.slug}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: i * 0.03 }}
                    >
                      <TopicCard
                        topic={topic}
                        onClick={() => navigate(`/gk/${category}/${topic.slug}`)}
                        progress={progressMap?.get(`${category}/${topic.slug}`)}
                      />
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Main GkCategory page ────────────────────────────────────────────────── */
export default function GkCategory() {
  const { category } = useParams();
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const isHistory    = category === "history";

  /* flat-list state (non-history categories) */
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(!isHistory);
  const [error, setError]       = useState(null);
  const [page, setPage]         = useState(1);

  /* shared filter state */
  const [examTag, setExamTag]     = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);

  /* study progress map: "category/slug" → progress record */
  const [progressMap, setProgressMap] = useState(new Map());
  useEffect(() => {
    if (!user) return;
    studyApi.all()
      .then(all => {
        const map = new Map();
        all.filter(r => r.itemType === 'gk').forEach(r => map.set(r.itemSlug, r));
        setProgressMap(map);
      })
      .catch(() => {});
  }, [user]);

  const activeFilters = [examTag, difficulty].filter(Boolean).length;

  /* fetch for non-history categories only */
  useEffect(() => {
    if (isHistory) return;
    setLoading(true);
    setError(null);
    gkApi
      .getByCategory(category, {
        examTag: examTag || undefined,
        difficulty: difficulty || undefined,
        page,
        limit: 18,
      })
      .then(setData)
      .catch(() => setError("Failed to load topics."))
      .finally(() => setLoading(false));
  }, [category, isHistory, examTag, difficulty, page]);

  /* history total for breadcrumb subtitle */
  const [historyTotal, setHistoryTotal] = useState(null);
  useEffect(() => {
    if (!isHistory) return;
    gkApi
      .getByCategory(category, { limit: 1 })
      .then(d => setHistoryTotal(d.pagination?.total ?? null))
      .catch(() => {});
  }, [category, isHistory]);

  const categoryLabel = isHistory
    ? "History"
    : (data?.topics?.[0]?.categoryLabel || category.replace(/-/g, " "));

  function clearFilters() {
    setExamTag(""); setDifficulty(""); setPage(1);
  }

  return (
    <div className="min-h-screen bg-[#F5F6FA] font-body">
      <SEOHead
        title={`${category.charAt(0).toUpperCase() + category.slice(1)} GK for NDA, CDS & CAPF 2026 | EduPulseAI`}
        description={`Complete ${category} general knowledge topics for NDA, CDS, AFCAT and CAPF exam preparation. Topic-wise notes, key facts and MCQ practice. Free access.`}
        keywords={`${category} for NDA, ${category} GK, ${category} for CDS, ${category} for CAPF, ${category} notes defence exam`}
        url={`/gk/${category}`}
        jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage", "name": `${category.charAt(0).toUpperCase() + category.slice(1)} GK for Defence Exams`, "url": `https://edupulseai.co.in/gk/${category}`, "publisher": { "@type": "Organization", "name": "EduPulseAI" } }}
      />
      <GkHeader category={category} />

      {/* Breadcrumb + title */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
            <button onClick={() => navigate("/gk")} className="hover:text-gray-700 transition-colors">
              GK Library
            </button>
            <ChevronRight size={12} />
            <span className="text-gray-700 font-semibold capitalize">{categoryLabel}</span>
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-sora font-extrabold text-xl text-gray-900 capitalize">{categoryLabel}</h1>
              {isHistory && historyTotal !== null && (
                <p className="text-xs text-gray-400 mt-0.5">{historyTotal} topics · Ancient · Medieval · Modern</p>
              )}
              {!isHistory && data && (
                <p className="text-xs text-gray-400 mt-0.5">{data.pagination.total} topics</p>
              )}
            </div>
            <button
              onClick={() => setFilterOpen(v => !v)}
              className="flex items-center gap-2 text-sm font-semibold border border-gray-200 bg-white px-4 py-2 rounded-xl shadow-sm hover:border-gray-300 transition-all"
            >
              <SlidersHorizontal size={14} />
              Filters
              {activeFilters > 0 && (
                <span
                  className="w-4 h-4 rounded-full text-white text-[10px] font-extrabold flex items-center justify-center"
                  style={{ background: "#C9A227" }}
                >
                  {activeFilters}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Filter panel */}
      <AnimatePresence>
        {filterOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden bg-white border-b border-gray-200 shadow-sm"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap gap-6">
              {/* Difficulty */}
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Difficulty</p>
                <div className="flex gap-1.5">
                  {DIFFICULTIES.map(d => {
                    const c = { easy: "#059669", medium: "#D97706", hard: "#DC2626" }[d];
                    return (
                      <button
                        key={d}
                        onClick={() => { setDifficulty(difficulty === d ? "" : d); setPage(1); }}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg border capitalize transition-all"
                        style={difficulty === d
                          ? { background: c, color: "#fff", borderColor: c }
                          : { background: "#fff", color: "#6B7280", borderColor: "#E5E7EB" }}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Exam */}
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Exam</p>
                <div className="flex flex-wrap gap-1.5">
                  {EXAMS.map(e => (
                    <button
                      key={e}
                      onClick={() => { setExamTag(examTag === e ? "" : e); setPage(1); }}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg border transition-all"
                      style={examTag === e
                        ? { background: "#1A2810", color: "#fff", borderColor: "#1A2810" }
                        : { background: "#fff", color: "#374151", borderColor: "#E5E7EB" }}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>

              {activeFilters > 0 && (
                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700 transition-colors"
                  >
                    <X size={13} /> Clear all
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">

        {/* ── HISTORY: three sections ─────────────────────────────────────── */}
        {isHistory && (
          <>
            {HISTORY_SECTIONS.map(section => (
              <HistorySection
                key={section.key}
                section={section}
                examTag={examTag}
                difficulty={difficulty}
                navigate={navigate}
                category={category}
                progressMap={progressMap}
              />
            ))}
          </>
        )}

        {/* ── OTHER CATEGORIES: flat paginated list ────────────────────── */}
        {!isHistory && (
          <>
            {loading && (
              <div className="flex justify-center py-16">
                <Loader2 size={26} className="animate-spin text-gray-300" />
              </div>
            )}
            {error && (
              <div className="text-center py-10 text-red-500 text-sm">{error}</div>
            )}
            {!loading && !error && data && (
              <>
                {data.topics.length === 0 ? (
                  <div className="text-center py-16">
                    <p className="text-gray-400 text-sm mb-2">No topics match the selected filters.</p>
                    <button onClick={clearFilters} className="text-yellow-600 text-sm font-semibold underline">
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {data.topics.map((topic, i) => (
                        <motion.div
                          key={topic.slug}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: i * 0.03 }}
                        >
                          <TopicCard
                            topic={topic}
                            onClick={() => navigate(`/gk/${category}/${topic.slug}`)}
                            progress={progressMap.get(`${category}/${topic.slug}`)}
                          />
                        </motion.div>
                      ))}
                    </div>

                    {data.pagination.pages > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-8">
                        <button
                          disabled={page === 1}
                          onClick={() => setPage(p => p - 1)}
                          className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 bg-white disabled:opacity-40 hover:bg-gray-50 transition-all"
                        >
                          ← Prev
                        </button>
                        <span className="text-sm text-gray-500">Page {page} of {data.pagination.pages}</span>
                        <button
                          disabled={page === data.pagination.pages}
                          onClick={() => setPage(p => p + 1)}
                          className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 bg-white disabled:opacity-40 hover:bg-gray-50 transition-all"
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
